import React, { useEffect, useRef } from "react";
import { Close as IconClose } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { closeSnackbar, useSnackbar, SnackbarKey } from "notistack";

export const useOcSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const activeSnackbarsRef = useRef<Set<SnackbarKey>>(new Set());

  const SnackBarCloseButton = (key: import("notistack").SnackbarKey) => {
    const size = "16px";
    return (
      <IconButton onClick={() => {
        closeSnackbar(key);
        activeSnackbarsRef.current.delete(key);
      }}>
        <IconClose style={{ color: "#fff", width: size, height: size }} />
      </IconButton>
    );
  };

  useEffect(() => {
    // Close all active snackbars on any click or keydown
    const handleClick = () => {
      activeSnackbarsRef.current.forEach((key) => {
        closeSnackbar(key);
      });
      activeSnackbarsRef.current.clear();
    };

    const handleKeyDown = () => {
      activeSnackbarsRef.current.forEach((key) => {
        closeSnackbar(key);
      });
      activeSnackbarsRef.current.clear();
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  interface SnackbarMessage {
    type: "error" | "success" | "warning" | "info";
    message: string;
  }

  const setOpenMessage = ({ type, message }: SnackbarMessage) => {
    let backgroundColor;
    switch (type) {
      case "error":
        backgroundColor = "#CC0033";
        break;
      case "success":
        backgroundColor = "#2FA84F";
        break;
      case "warning":
        backgroundColor = "#F3AA18";
        break;
      default:
        backgroundColor = "#2FA84F";
    }

    const snackBarProps = {
      variant: type,
      autoHideDuration: type === "error" ? 16000 : 6000,
      anchorOrigin: {
        vertical: "top" as "top" | "bottom",
        horizontal: "center" as "left" | "center" | "right",
      },
      style: {
        backgroundColor: backgroundColor,
        fontSize: "16px",
        letterSpacing: "0.25px",
        minWidth: "auto",
      },

      action: (key: import("notistack").SnackbarKey) =>
        SnackBarCloseButton(key),

      onExited: (event: any, key: SnackbarKey) => {
        activeSnackbarsRef.current.delete(key);
      },
    };

    const key = enqueueSnackbar(message, snackBarProps);
    activeSnackbarsRef.current.add(key);
  };
  return { setOpenMessage };
};

export default useOcSnackbar;
